import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { LABELS } from "@match/shared";
import { PageHeader, EmptyState, RoleBadge } from "@/components/Layout";
import { AccountPanel, ClientPanel } from "@/components/forms";
import { ClientLedger, ReconciliationBox } from "@/components/ledger";
import { PairView } from "@/components/pair";
import { Button, ConnectionState, ErrorNote, useCanWrite } from "@/components/ui";
import { ArchiveAccount, DeleteForever } from "@/components/delete";
import { api, errorMessage } from "@/lib/api";
import { dateTime, money } from "@/lib/format";
import { useClient, useRefreshDirectory } from "@/lib/queries";
import type { Account, ClientDetail as ClientDetailData, ProxyCheck } from "@/lib/types";

export function ClientDetail() {
  const { id = "" } = useParams();
  const canWrite = useCanWrite();
  const client = useClient(id);
  const [editClient, setEditClient] = useState(false);
  const [accountPanel, setAccountPanel] = useState<{ open: boolean; account: Account | null; role: "PROP" | "BROKER" | "NONE" }>({
    open: false,
    account: null,
    role: "PROP",
  });

  if (client.isLoading) return <p className="px-6 py-6 text-sm text-muted">Caricamento…</p>;
  if (client.error || !client.data) {
    return (
      <div className="px-6 py-6">
        <ErrorNote>{client.error ? errorMessage(client.error) : "Cliente non trovato"}</ErrorNote>
        <Link to="/clienti" className="mt-3 inline-block text-sm text-muted hover:text-ink">
          Torna ai clienti
        </Link>
      </div>
    );
  }

  const c = client.data;
  // La coppia: il conto prop e il conto broker più recenti (il backend li ordina così).
  const prop = c.accounts.find((a) => a.role === "PROP") ?? null;
  const broker = c.accounts.find((a) => a.role === "BROKER") ?? null;
  const others = c.accounts.filter((a) => a !== prop && a !== broker);

  const addAccount = (role: "PROP" | "BROKER" | "NONE") => setAccountPanel({ open: true, account: null, role });
  const editAccount = (account: Account) => setAccountPanel({ open: true, account, role: account.role });

  return (
    <>
      <PageHeader
        title={c.name}
        subtitle={c.email}
        actions={
          canWrite && (
            <>
              <Button onClick={() => setEditClient(true)}>Modifica cliente</Button>
              <Button variant="primary" onClick={() => addAccount(!prop ? "PROP" : !broker ? "BROKER" : "NONE")}>
                Aggiungi conto
              </Button>
            </>
          )
        }
      />

      <div className="space-y-6 px-6 py-5">
        <section className="flex flex-wrap gap-x-10 gap-y-3 text-sm">
          <div>
            <p className="text-xs text-faint">CID</p>
            {/*
              * Uno solo, e non c'e' piu' un «CID del ciclo» da confrontare:
              * prima questo riquadro doveva avvisare quando i conti andavano a
              * mercato con un CID diverso da quello mostrato qui — cioe' quando
              * la copia sul ciclo era divergita. Adesso la copia non decide
              * niente, e quell'avviso non ha piu' un caso da segnalare.
              */}
            <p className="machine-id mt-0.5">{c.computerId}</p>
            <p className="mt-0.5 text-xs text-faint">Generato alla creazione, non cambia mai</p>
          </div>
          <ProxyBox client={c} canWrite={canWrite} />
          {c.notes && (
            <div className="max-w-md">
              <p className="text-xs text-faint">Note</p>
              <p className="mt-0.5 text-ink">{c.notes}</p>
            </div>
          )}
        </section>

        <section>
          <h2 className="mb-2 text-sm font-medium text-ink">Coppia</h2>
          <PairView prop={prop} broker={broker} canWrite={canWrite} onEdit={editAccount} onAdd={addAccount} />
        </section>

        {others.length > 0 && (
          <section>
            <h2 className="mb-2 text-sm font-medium text-ink">Altri conti</h2>
            <div className="card divide-y divide-line">
              {others.map((a) => (
                <div key={a.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-sm">
                  <RoleBadge role={a.role} />
                  <span className="font-medium text-ink">{a.label}</span>
                  <span className="machine-id">{a.mt5Login}</span>
                  <span className="text-muted">{a.server.displayName ?? a.server.name}</span>
                  <span className="italic text-muted">{a.lastBalance === null ? "—" : money(a.lastBalance, a.currency)}</span>
                  <ConnectionState connected={a.isConnected} error={a.lastConnectError} />
                  {canWrite && (
                    <span className="ml-auto inline-flex flex-wrap items-center gap-2">
                      <Button size="sm" variant="ghost" onClick={() => editAccount(a)}>
                        Modifica
                      </Button>
                      {/* Le due meta' della stessa scelta, e si mostrano insieme:
                          archiviare conserva i riferimenti, eliminare li toglie.
                          Un conto nato col login sbagliato va ELIMINATO, perche'
                          archiviarlo non libererebbe quel numero MT5. */}
                      <ArchiveAccount id={a.id} label={a.label} size="sm" />
                      <DeleteForever kind="conto" id={a.id} label={a.label} size="sm" />
                    </span>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {c.accounts.some((a) => a.role !== "NONE") && (
          <section>
            <h2 className="mb-2 text-sm font-medium text-ink">Riconciliazione dei conti</h2>
            <div className="grid gap-4 md:grid-cols-2">
              {[prop, broker, ...others.filter((a) => a.role !== "NONE")].filter((a): a is Account => !!a).map((a) => (
                <ReconciliationBox key={a.id} account={a} />
              ))}
            </div>
          </section>
        )}

        <ClientLedger client={c} />

        <section>
          <h2 className="mb-2 text-sm font-medium text-ink">Cicli</h2>
          {c.cycles.length === 0 ? (
            <EmptyState
              title="Nessun ciclo"
              hint="Il ciclo parte con la prima fase: collega la coppia, poi crea la fase dalla pagina Fasi."
            />
          ) : (
            <div className="card divide-y divide-line">
              {c.cycles.map((cy) => (
                <div key={cy.id} className="flex flex-wrap items-center gap-x-4 px-4 py-2.5 text-sm">
                  <span className="font-medium text-ink">{cy.label ?? `${cy.propFirm.name} ${cy.preset.name}`}</span>
                  <span className="text-muted">{LABELS.cyclePhase[cy.currentPhase]}</span>
                  {/* La taglia è nella valuta del conto prop. */}
                  <span className="text-muted">{money(cy.accountSize, prop?.currency ?? "USD")}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <ClientPanel open={editClient} client={c} onClose={() => setEditClient(false)} />
      <AccountPanel
        open={accountPanel.open}
        clientId={c.id}
        clientDefaults={{ computerId: c.computerId, defaultProxy: c.defaultProxy }}
        account={accountPanel.account}
        defaultRole={accountPanel.role}
        onClose={() => setAccountPanel((p) => ({ ...p, open: false, account: null }))}
      />
    </>
  );
}

/**
 * Il proxy del cliente. Sta qui perche' e' suo: la pagina Proxy non esiste piu'
 * (DECISIONE 19), e con lei se ne sarebbe andato anche il controllo di
 * raggiungibilita' — che e' l'unico modo di sapere che un proxy e' morto prima
 * che lo dica un'apertura fallita.
 */
function ProxyBox({ client: c, canWrite }: { client: ClientDetailData; canWrite: boolean }) {
  const refresh = useRefreshDirectory();
  const [busy, setBusy] = useState(false);
  const [check, setCheck] = useState<ProxyCheck | null>(null);
  const [error, setError] = useState<string | null>(null);
  const p = c.defaultProxy;

  const controlla = async () => {
    if (!p) return;
    setBusy(true);
    setError(null);
    setCheck(null);
    try {
      setCheck((await api.proxies.check(p.id)).check);
      await refresh();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!p) {
    return (
      <div>
        <p className="text-xs text-faint">Proxy</p>
        <p className="mt-0.5 text-warn">Nessuno: i conti prop non si connettono</p>
      </div>
    );
  }

  return (
    <div className="max-w-md">
      <p className="text-xs text-faint">Proxy</p>
      <p className="mt-0.5 text-ink">
        {p.label ? `${p.label} · ` : ""}
        {p.host}:{p.port}
        {[p.city, p.country].filter(Boolean).length ? ` · ${[p.city, p.country].filter(Boolean).join(", ")}` : ""}
      </p>
      <p className="text-xs text-faint">
        {p.type}
        {p.username ? ` · ${p.username}` : ""}
        {p.sessionId ? (
          <>
            {" · sessione "}
            <span className="machine-id">{p.sessionId}</span>
          </>
        ) : null}
      </p>
      <p className="mt-1 flex flex-wrap items-center gap-2 text-xs">
        {p.lastCheckAt ? (
          <span className={p.lastCheckOk ? "text-gain" : "text-loss"}>
            {dateTime(p.lastCheckAt)} · {p.lastCheckOk ? "raggiungibile" : "non raggiungibile"}
          </span>
        ) : (
          <span className="text-muted">Mai controllato</span>
        )}
        {canWrite && (
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => void controlla()}>
            {busy ? "Controllo…" : "Controlla"}
          </Button>
        )}
      </p>
      {check && <p className={`text-xs ${check.ok ? "text-gain" : "text-loss"}`}>{check.detail} · {check.elapsedMs} ms</p>}
      {error && <p className="text-xs text-loss">{error}</p>}
    </div>
  );
}
